import { OreHeading, OreInput, OreStack } from '@edenredespana/oreneta'
import { useEffect, useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AutoEnrollmentTranslation } from '@/AutoEnrollment/ui/autoEnrollmentTranslation'
import { Step1Form } from '@/AutoEnrollment/ui/hooks'
import { handleErrors } from '@/Shared/ui/form'
import { TransformObject, useGooglePlaces } from '@/Store/ui/hooks'

export const Step1BillingAddressData = (): JSX.Element => {
  const { t } = useTranslation()
  const formContext = useFormContext<Step1Form>()
  const { register, formState, getValues } = formContext
  const { errors } = handleErrors(formState)
  const { ref, ...rest } = register('billingStreet')

  const transformObject: TransformObject<Step1Form> = {
    street: 'billingStreet',
    number: 'billingNumber',
    floorBlockStair: 'billingFloorBlockStair',
    postalCode: 'billingPostalCode',
    city: 'billingCity',
    province: 'billingProvince',
  }

  const inputReference = useRef<HTMLInputElement | null>(null)
  const { initGooglePlaces } = useGooglePlaces({ formContext, transformObject })

  useEffect(() => {
    initGooglePlaces(inputReference)
  }, [])

  return (
    <OreStack placeContent="stretch" space="large">
      <OreHeading as="h2" size="headline-md">
        {t(AutoEnrollmentTranslation.billingAddressData.title)}
      </OreHeading>
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...rest}
          {...errors('billingStreet')}
          id="street"
          label={t(AutoEnrollmentTranslation.billingAddressData.data.street.label)}
          placeholder={t(
            AutoEnrollmentTranslation.billingAddressData.data.street.placeholder,
          )}
          labelTooltip={t(
            AutoEnrollmentTranslation.billingAddressData.data.street.tooltip,
          )}
          required
          ref={(input: HTMLInputElement) => {
            inputReference.current = input
            ref(input)
          }}
          defaultValue={getValues('billingStreet') ?? ''}
          autoComplete={'off'}
          role="presentation"
          maxLength={255}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('billingNumber')}
          {...errors('billingNumber')}
          id="number"
          label={t(AutoEnrollmentTranslation.billingAddressData.data.number.label)}
          maxLength={255}
          required
        />
        <OreInput
          {...register('billingFloorBlockStair')}
          {...errors('billingFloorBlockStair')}
          id="floorBlockStair"
          label={t(
            AutoEnrollmentTranslation.billingAddressData.data.floorBlockStair.label,
          )}
          maxLength={255}
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch" sameSize>
        <OreInput
          {...register('billingPostalCode')}
          {...errors('billingPostalCode')}
          id="postalCode"
          label={t(AutoEnrollmentTranslation.billingAddressData.data.postalCode.label)}
          maxLength={255}
          required
        />
        <OreInput
          {...register('billingCity')}
          {...errors('billingCity')}
          id="city"
          label={t(AutoEnrollmentTranslation.billingAddressData.data.city.label)}
          maxLength={255}
          required
        />
        <OreInput
          {...register('billingProvince')}
          {...errors('billingProvince')}
          id="province"
          label={t(AutoEnrollmentTranslation.billingAddressData.data.province.label)}
          maxLength={255}
          required
        />
      </OreStack>
      <OreStack direction="row" space="large" placeContent="stretch">
        <OreInput
          {...register('billingAdditionalInfo')}
          {...errors('billingAdditionalInfo')}
          id="billingAdditionalInfo"
          label={t(
            AutoEnrollmentTranslation.billingAddressData.data.additionalInfo.label,
          )}
          placeholder={t(
            AutoEnrollmentTranslation.billingAddressData.data.additionalInfo.placeholder,
          )}
          maxLength={255}
        />
      </OreStack>
    </OreStack>
  )
}
